import React, { useState, useEffect } from 'react';
import { marketing } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Sparkles, X, Loader2 } from 'lucide-react';

const INTEREST_OPTIONS = [
  'Food & Drink', 'Health & Wellness', 'Beauty & Salons', 'Fashion & Style',
  'Fitness & Recreation', 'Family & Kids', 'Arts & Culture', 'Home & Services',
  'Business & Finance', 'Tech', 'Education', 'Travel',
];
const LIFESTYLE_OPTIONS = [
  'Family-oriented', 'Eco-conscious', 'Fitness-focused', 'Foodie',
  'Homebody', 'Nightlife', 'Faith-based', 'Entrepreneur', 'Student',
];
// US-state dropdown (postal codes) — kept minimal to avoid an autocomplete
// dataset per the user's "free-text city + state dropdown" choice.
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

// Post-signup onboarding survey. Optional but persistent — the modal
// reappears at each login until either completed OR dismissed.
export default function OnboardingSurveyModal() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [interests, setInterests] = useState([]);
  const [lifestyle, setLifestyle] = useState([]);
  const [dob, setDob] = useState('');
  const [homeCity, setHomeCity] = useState('');
  const [homeState, setHomeState] = useState('');
  const [specialDates, setSpecialDates] = useState([{ label: '', date: '' }]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user.welcomePending) return;
    try { if (sessionStorage.getItem('pbb_welcome_shown')) return; } catch {}
    const s = user.onboardingSurvey || {};
    // Show if not completed AND not dismissed. Once dismissed, they can still
    // resurface it from the Profile page (future) — for now honor the choice.
    if (!s.completedAt && !s.dismissed) setOpen(true);
  }, [user]);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  const submit = async () => {
    setBusy(true);
    try {
      const cleanDates = specialDates.filter(d => d.label.trim() && d.date);
      const homeLoc = (homeCity.trim() && homeState) ? { city: homeCity.trim(), state: homeState } : null;
      await marketing.saveOnboarding({
        interests, lifestyle, dob: dob || null, specialDates: cleanDates,
        homeLocation: homeLoc, additionalLocations: [],
      });
      toast({ title: 'Preferences saved — +25 pts' });
      await refresh();
      setOpen(false);
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    setBusy(true);
    try { await marketing.dismissOnboarding(); setOpen(false); await refresh(); }
    catch { toast({ title: 'Could not dismiss' }); }
    finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-50 p-4 overflow-y-auto"
      data-testid="onboarding-modal">
      <div className="bg-[#0d0d0d] border border-[#2a2a2a] rounded-2xl max-w-lg w-full my-8">
        <div className="p-5 border-b border-[#2a2a2a] flex items-start justify-between gap-3">
          <div>
            <div className="text-white font-bold text-lg inline-flex items-center gap-2">
              <Sparkles size={18} className="text-yellow-400"/> Personalize your feed
            </div>
            <div className="text-xs text-gray-400 mt-0.5">Tell us what you love — you'll only see banners and deals that match. +25 loyalty pts for finishing.</div>
          </div>
          <button type="button" onClick={dismiss} disabled={busy} data-testid="onboarding-close"
            className="text-gray-400 hover:text-white p-1"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="text-[11px] text-gray-400 mb-2 font-semibold uppercase tracking-wider">Categories you care about</div>
            <div className="flex flex-wrap gap-1.5">
              {INTEREST_OPTIONS.map(o => (
                <button key={o} type="button" onClick={() => toggle(interests, setInterests, o)}
                  data-testid={`onboarding-interest-${o.replace(/[^a-z0-9]/gi,'-').toLowerCase()}`}
                  className={`text-xs px-2.5 py-1 rounded-full border ${interests.includes(o) ? 'border-green-500 bg-green-500/10 text-white' : 'border-[#2a2a2a] text-gray-300 hover:border-gray-500'}`}>
                  {o}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-gray-400 mb-2 font-semibold uppercase tracking-wider">Lifestyle</div>
            <div className="flex flex-wrap gap-1.5">
              {LIFESTYLE_OPTIONS.map(o => (
                <button key={o} type="button" onClick={() => toggle(lifestyle, setLifestyle, o)}
                  data-testid={`onboarding-lifestyle-${o.replace(/[^a-z0-9]/gi,'-').toLowerCase()}`}
                  className={`text-xs px-2.5 py-1 rounded-full border ${lifestyle.includes(o) ? 'border-green-500 bg-green-500/10 text-white' : 'border-[#2a2a2a] text-gray-300 hover:border-gray-500'}`}>
                  {o}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] text-gray-400 mb-1 font-semibold uppercase tracking-wider">Your home city</div>
            <div className="grid grid-cols-3 gap-2">
              <input value={homeCity} onChange={e => setHomeCity(e.target.value)}
                data-testid="onboarding-home-city" maxLength={80}
                placeholder="City (e.g. Atlanta)"
                className="pbb-input col-span-2"/>
              <select value={homeState} onChange={e => setHomeState(e.target.value)}
                data-testid="onboarding-home-state"
                className="pbb-input">
                <option value="">State</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="text-[10px] text-gray-500 mt-1">Local businesses will show up first on your homepage banner. You can add more cities later in My Interests.</div>
          </div>

          <label className="block">
            <div className="text-[11px] text-gray-400 mb-1 font-semibold uppercase tracking-wider">Your birthday (used for surprise deals)</div>
            <input type="date" value={dob} onChange={e => setDob(e.target.value)}
              data-testid="onboarding-dob" className="pbb-input"/>
          </label>

          <div>
            <div className="text-[11px] text-gray-400 mb-1 font-semibold uppercase tracking-wider">Special dates (anniversary, kids' birthdays…)</div>
            {specialDates.map((sd, i) => (
              <div key={i} className="flex gap-2 mt-1.5">
                <input placeholder="Label (e.g. Anniversary)" value={sd.label}
                  onChange={e => setSpecialDates(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                  data-testid={`onboarding-special-label-${i}`}
                  className="pbb-input flex-1"/>
                <input type="date" value={sd.date}
                  onChange={e => setSpecialDates(prev => prev.map((x, j) => j === i ? { ...x, date: e.target.value } : x))}
                  data-testid={`onboarding-special-date-${i}`}
                  className="pbb-input flex-1"/>
              </div>
            ))}
            <button type="button" onClick={() => setSpecialDates(prev => [...prev, { label: '', date: '' }])}
              data-testid="onboarding-special-add"
              className="mt-2 text-xs text-green-400 hover:text-green-300">+ add another date</button>
          </div>
        </div>

        <div className="p-5 pt-0 flex gap-2">
          <button type="button" onClick={dismiss} disabled={busy} data-testid="onboarding-later"
            className="flex-1 px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-gray-500 text-gray-200 text-sm">
            Maybe later
          </button>
          <button type="button" onClick={submit} disabled={busy} data-testid="onboarding-submit"
            className="flex-1 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm inline-flex items-center justify-center gap-1 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>} Save & earn 25 pts
          </button>
        </div>
      </div>
    </div>
  );
}
